import React from "react";
import { DestinationMarquee } from "components/content";
import { Page } from "components/page";
import {
  PrivacyAndComplianceFeatures,
  ActivationFeatures,
  CampaignUseCases,
  ReachAndConversionFeatures,
} from "components/solutions/advertising";
import { StackedLogos } from "components/logos";
import { type NextPage } from "next";
import { getLandingPageProps, getPageMetaImage, getSafeCustomer, type LandingPageProps } from "utils";
import { AuraCardQuote } from "components/quotes";
import { Text } from "@chakra-ui/react";
import { CustomerStatsHeroAnimation, GradientHalfHero } from "components/hero";
import heroBgPhoto from "../../../public/assets/solutions/advertising/hero-bg-photo.png";

const Advertising: NextPage<LandingPageProps> = ({
  page,
  destinations,
  announcementBanner,
  defaultLogos,
  defaultMobileLogos,
  customers,
}) => {
  const lucid = getSafeCustomer(customers, "lucidchart");
  const bol = getSafeCustomer(customers, "bol");
  const weightWatchers = getSafeCustomer(customers, "weight-watchers");
  const velir = getSafeCustomer(customers, "velir");

  return (
    <Page
      title={page.metaTitle}
      description={page.metaDescription}
      image={getPageMetaImage({ src: page.metaImageUrl, alt: page.metaTitle })}
      announcementBanner={announcementBanner}
      bgColor="new.blue.150"
      footerOptions={{ variant: "blue" }}
    >
      <GradientHalfHero
        variant="blue"
        eyebrow={page.eyebrow}
        title={page.title}
        description={page.description}
        media={
          <CustomerStatsHeroAnimation
            bgPhoto={{
              src: heroBgPhoto,
              alt: "A group of four people appearing to work collaboratively together in a coffee shop setting. One is working on a laptop, one is working on a tablet, and the last two are having a conversation with each other.",
            }}
            primaryCaseStudyStat={{
              customer: lucid,
              percentValue: 52,
              description: "ROAS",
            }}
            secondaryCaseStudyStat={{
              customer: bol,
              percentValue: 33,
              description: "Click-through rates",
            }}
          />
        }
        mb={{ sm: "small", md: "big", lg: 0 }}
      />

      <StackedLogos customers={defaultLogos} mobileCustomers={defaultMobileLogos} my="0" />

      <ActivationFeatures />

      <AuraCardQuote
        variant="blue"
        quote={{
          quoteText: (
            <>
              With Hightouch, I’m able to build any audience{" "}
              <Text as="span" fontWeight="bold">
                within a matter of minutes
              </Text>{" "}
              and sync it to our ad platform immediately.
            </>
          ),
          author: { name: "Annie Davis", job: "Senior Manager, Paid Social" },
          company: {
            name: "Weight Watchers",
            logo: weightWatchers.logoUrl,
          },
          cta: { text: "Read case study", href: "/customers/weight-watchers" },
        }}
      />

      <DestinationMarquee
        destinations={destinations}
        title="Connect to any media platform"
        description="Send any data to any platform you run ads in."
        ctaText="See all advertising integrations"
        ctaSearchParams="?category=Advertising"
        textMaxW="3xl"
      />

      <CampaignUseCases />

      <ReachAndConversionFeatures
        quote={{
          quoteText: "It’s like pushing the easy button for improving ad performance.",
          author: { name: "Adam Ribaudo", job: "VP of Data Activation" },
          company: { name: "Velir", logo: velir.logoUrl },
        }}
      />

      <PrivacyAndComplianceFeatures />
    </Page>
  );
};

export default Advertising;

export const getStaticProps = getLandingPageProps("/solutions/advertising", {
  loadDefaultLogos: true,
  loadDefaultMobileLogos: true,
  loadDestinations: "Advertising",
  loadCustomers: ["lucidchart", "bol", "weight-watchers", "velir"],
});
